import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    createServer,
    createServiceDefinition,
    createServiceInstance,
    deleteServer,
    deleteServiceDefinition,
    deleteServiceInstance,
    updateServer,
    updateServiceDefinition,
    updateServiceInstance,
} from '@/api/configuration';
import type { ServerAction, ServiceAction } from '@/api/deploy';
import { getDeploymentDetail, getDeploymentStatus, manageServer, manageService } from '@/api/deploy';
import { useEnvironments, useInvalidateConfiguration, useServers, useServiceDefinitions, useServiceInstances } from '@/hooks/use-configuration';
import { useEnvironmentStore } from '@/stores/environment-store';

import { Server, ServiceDefinition, ServiceInstance } from '@/api/configuration';
import { EnvironmentSelector } from '@/components/shared/environment-selector';

// --- Server Status Types ---

type ServerStatus = 'unknown' | 'checking' | 'running' | 'stopped';

function StatusIndicator({ status }: { status: ServerStatus }) {
  const config: Record<ServerStatus, { color: string; label: string }> = {
    unknown: { color: 'bg-gray-400', label: 'Unknown' },
    checking: { color: 'bg-yellow-400 animate-pulse', label: 'Checking...' },
    running: { color: 'bg-green-500', label: 'Running' },
    stopped: { color: 'bg-red-500', label: 'Stopped' },
  };

  const { color, label } = config[status];

  return (
    <span className="inline-flex items-center gap-1.5" title={label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} aria-hidden="true" title="{label}" />
    </span>
  );
}

// --- Shared UI Components ---

function SectionHeader({
  title,
  onAdd,
  addLabel,
}: {
  title: string;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-content">{title}</h3>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 transition-colors"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        {addLabel}
      </button>
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  variant = 'default',
}: {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}) {
  const base = 'rounded px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1';
  const styles =
    variant === 'danger'
      ? `${base} text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:ring-red-500`
      : `${base} text-content-secondary hover:bg-surface-raised focus:ring-brand-600`;
  return (
    <button type="button" onClick={onClick} className={styles}>
      {label}
    </button>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-content-tertiary">{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// --- Server Form ---

function ServerForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Server;
  onSave: (data: Partial<Server>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [inventoryName, setInventoryName] = useState(initial?.inventoryName ?? '');
  const [type, setType] = useState(initial?.type ?? '');
  const [awsInstanceId, setAwsInstanceId] = useState(initial?.awsInstanceId ?? '');
  const [awsRegion, setAwsRegion] = useState(initial?.awsRegion ?? '');
  const [awsProfile, setAwsProfile] = useState(initial?.awsProfile ?? '');
  // Ansible connection fields
  const [ansibleHost, setAnsibleHost] = useState(initial?.ansibleHost ?? '');
  const [ansibleUser, setAnsibleUser] = useState(initial?.ansibleUser ?? '');
  const [ansibleOs, setAnsibleOs] = useState(initial?.ansibleOs ?? 'linux');
  const [ansiblePort, setAnsiblePort] = useState(initial?.ansiblePort?.toString() ?? '');
  const [ansibleBecomeMethod, setAnsibleBecomeMethod] = useState(initial?.ansibleBecomeMethod ?? '');
  const [sshKeyRef, setSshKeyRef] = useState(initial?.sshKeyRef ?? '');
  const [ansiblePassword, setAnsiblePassword] = useState(initial?.ansiblePassword ?? '');
  // WinRM fields
  const [ansibleWinrmScheme, setAnsibleWinrmScheme] = useState(initial?.ansibleWinrmScheme ?? 'http');
  const [ansibleWinrmTransport, setAnsibleWinrmTransport] = useState(initial?.ansibleWinrmTransport ?? 'ntlm');
  const [ansibleWinrmCertValidation, setAnsibleWinrmCertValidation] = useState(initial?.ansibleWinrmCertValidation ?? 'ignore');

  const isWindows = ansibleOs === 'windows';
  // EC2 servers can be powered on/off/checked via the AWS CLI path in the deploy
  // service, which gates on `type === "EC2"` and a non-blank instance ID.
  const isEc2 = type === 'EC2';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      inventoryName: inventoryName || undefined,
      type: type || undefined,
      awsInstanceId: awsInstanceId || undefined,
      awsRegion: awsRegion || undefined,
      awsProfile: awsProfile || undefined,
      ansibleHost: ansibleHost || undefined,
      ansibleUser: ansibleUser || undefined,
      ansibleOs: ansibleOs || undefined,
      ansiblePort: ansiblePort ? parseInt(ansiblePort, 10) : undefined,
      ansibleBecomeMethod: ansibleBecomeMethod || undefined,
      sshKeyRef: !isWindows && sshKeyRef ? sshKeyRef : undefined,
      ansiblePassword: ansiblePassword || undefined,
      ansibleWinrmScheme: isWindows ? ansibleWinrmScheme : undefined,
      ansibleWinrmTransport: isWindows ? ansibleWinrmTransport : undefined,
      ansibleWinrmCertValidation: isWindows ? ansibleWinrmCertValidation : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-4">
      {/* Basic Information */}
      <fieldset>
        <legend className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">Basic Information</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label htmlFor="srv-name" className="block text-xs font-medium text-content-secondary mb-1">Name *</label>
            <input id="srv-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-inventory-name" className="block text-xs font-medium text-content-secondary mb-1">Inventory Name</label>
            <input id="srv-inventory-name" type="text" value={inventoryName} onChange={(e) => setInventoryName(e.target.value)} placeholder="Short host key for Ansible" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-type" className="block text-xs font-medium text-content-secondary mb-1">Type</label>
            <select id="srv-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
              <option value="">— None —</option>
              <option value="EC2">EC2</option>
              <option value="VM">VM</option>
              <option value="Azure">Azure</option>
            </select>
            {type === 'EC2' && (
              <p className="mt-1 text-xs text-content-tertiary">Set the Instance ID and Region below to enable start/stop/check.</p>
            )}
          </div>
        </div>
      </fieldset>

      {/* Ansible Connection */}
      <fieldset>
        <legend className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">Ansible Connection</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label htmlFor="srv-ansible-host" className="block text-xs font-medium text-content-secondary mb-1">Host / IP *</label>
            <input id="srv-ansible-host" type="text" required value={ansibleHost} onChange={(e) => setAnsibleHost(e.target.value)} placeholder="10.0.1.42 or hostname" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-ansible-user" className="block text-xs font-medium text-content-secondary mb-1">Remote User</label>
            <input id="srv-ansible-user" type="text" value={ansibleUser} onChange={(e) => setAnsibleUser(e.target.value)} placeholder={isWindows ? 'Administrator' : 'ec2-user'} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-ansible-os" className="block text-xs font-medium text-content-secondary mb-1">OS</label>
            <select id="srv-ansible-os" value={ansibleOs} onChange={(e) => setAnsibleOs(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
              <option value="linux">Linux</option>
              <option value="windows">Windows</option>
            </select>
          </div>
          <div>
            <label htmlFor="srv-ansible-port" className="block text-xs font-medium text-content-secondary mb-1">Port</label>
            <input id="srv-ansible-port" type="number" value={ansiblePort} onChange={(e) => setAnsiblePort(e.target.value)} placeholder={isWindows ? '5986' : '22'} min={1} max={65535} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-become-method" className="block text-xs font-medium text-content-secondary mb-1">Become Method</label>
            <select id="srv-become-method" value={ansibleBecomeMethod} onChange={(e) => setAnsibleBecomeMethod(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
              <option value="">None</option>
              <option value="sudo">sudo</option>
              <option value="su">su</option>
              <option value="runas">runas</option>
            </select>
          </div>
          <div>
            <label htmlFor="srv-ansible-password" className="block text-xs font-medium text-content-secondary mb-1">Password</label>
            <input id="srv-ansible-password" type="password" value={ansiblePassword} onChange={(e) => setAnsiblePassword(e.target.value)} placeholder="Connection password" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
        </div>
      </fieldset>

      {/* SSH Key (Linux only) */}
      {!isWindows && (
        <fieldset>
          <legend className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">SSH Key</legend>
          <div>
            <label htmlFor="srv-ssh-key-ref" className="block text-xs font-medium text-content-secondary mb-1">S3 Key Reference</label>
            <input id="srv-ssh-key-ref" type="text" value={sshKeyRef} onChange={(e) => setSshKeyRef(e.target.value)} placeholder="s3://corvus-ssh-keys/project/key.pem" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
        </fieldset>
      )}

      {/* WinRM (Windows only) */}
      {isWindows && (
        <fieldset>
          <legend className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">WinRM Configuration</legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="srv-winrm-scheme" className="block text-xs font-medium text-content-secondary mb-1">Scheme</label>
              <select id="srv-winrm-scheme" value={ansibleWinrmScheme} onChange={(e) => setAnsibleWinrmScheme(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
              </select>
            </div>
            <div>
              <label htmlFor="srv-winrm-transport" className="block text-xs font-medium text-content-secondary mb-1">Transport</label>
              <select id="srv-winrm-transport" value={ansibleWinrmTransport} onChange={(e) => setAnsibleWinrmTransport(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
                <option value="ntlm">NTLM</option>
                <option value="kerberos">Kerberos</option>
                <option value="credssp">CredSSP</option>
                <option value="basic">Basic</option>
              </select>
            </div>
            <div>
              <label htmlFor="srv-winrm-cert" className="block text-xs font-medium text-content-secondary mb-1">Cert Validation</label>
              <select id="srv-winrm-cert" value={ansibleWinrmCertValidation} onChange={(e) => setAnsibleWinrmCertValidation(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
                <option value="validate">Validate</option>
                <option value="ignore">Ignore</option>
              </select>
            </div>
          </div>
        </fieldset>
      )}

      {/* AWS Metadata */}
      <fieldset>
        <legend className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">
          AWS Metadata
          {isEc2 && <span className="ml-2 font-normal normal-case tracking-normal text-content-tertiary">(Instance ID required to control this EC2 instance)</span>}
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label htmlFor="srv-instance" className="block text-xs font-medium text-content-secondary mb-1">Instance ID{isEc2 ? ' *' : ''}</label>
            <input id="srv-instance" type="text" required={isEc2} value={awsInstanceId} onChange={(e) => setAwsInstanceId(e.target.value)} placeholder="i-0abc123..." className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-region" className="block text-xs font-medium text-content-secondary mb-1">Region</label>
            <input id="srv-region" type="text" value={awsRegion} onChange={(e) => setAwsRegion(e.target.value)} placeholder="eu-west-2" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
          <div>
            <label htmlFor="srv-profile" className="block text-xs font-medium text-content-secondary mb-1">Profile</label>
            <input id="srv-profile" type="text" value={awsProfile} onChange={(e) => setAwsProfile(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          </div>
        </div>
      </fieldset>

      <div className="flex gap-2 pt-1">
        <button type="submit" className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          {initial ? 'Update' : 'Add Server'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          Cancel
        </button>
      </div>
    </form>
  );
}
// --- Service Definition Form ---

function ServiceDefinitionForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: ServiceDefinition;
  onSave: (data: { name: string; port?: number; type?: string; serviceName?: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [port, setPort] = useState(initial?.port?.toString() ?? '');
  const [type, setType] = useState(initial?.type ?? '');
  const [serviceName, setServiceName] = useState(initial?.serviceName ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      port: port ? parseInt(port, 10) : undefined,
      type: type || undefined,
      serviceName: serviceName || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-3">
      <p className="text-xs font-semibold text-content-secondary">
        {initial ? 'Edit Service Definition' : 'New Service Definition'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label htmlFor="sd-name" className="block text-xs font-medium text-content-secondary mb-1">Name *</label>
          <input id="sd-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="My Service" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="sd-type" className="block text-xs font-medium text-content-secondary mb-1">Type</label>
          <select id="sd-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
            <option value="">— None —</option>
            <option value="SYSTEM">System (systemd)</option>
            <option value="JBOSS">JBoss</option>
            <option value="IIS">IIS</option>
          </select>
        </div>
        <div>
          <label htmlFor="sd-port" className="block text-xs font-medium text-content-secondary mb-1">Port</label>
          <input id="sd-port" type="number" value={port} onChange={(e) => setPort(e.target.value)} placeholder="8080" min={1} max={65535} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="sd-service-name" className="block text-xs font-medium text-content-secondary mb-1">Service Name</label>
          <input id="sd-service-name" type="text" value={serviceName} onChange={(e) => setServiceName(e.target.value)} placeholder="my-app.service" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          {initial ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Environment Override (inline within a service definition card) ---

function ServiceOverridePanel({
  definition,
  servers,
  instance,
  onSave,
  onDelete,
}: {
  definition: ServiceDefinition;
  servers: Server[];
  instance?: ServiceInstance;
  onSave: (data: { serviceDefinitionId: string; serverId: string; serviceName?: string; port?: number }) => void;
  onDelete?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [serverId, setServerId] = useState(instance?.serverId ?? '');
  const [serviceName, setServiceName] = useState(instance?.serviceName ?? '');
  const [port, setPort] = useState(instance?.port?.toString() ?? '');

  // Auto-select server if there's only one
  useEffect(() => {
    if (!serverId && servers.length === 1) {
      setServerId(servers[0]!.id);
    }
  }, [servers, serverId]);

  // Reset form when instance changes (e.g. environment switch)
  useEffect(() => {
    setServerId(instance?.serverId ?? (servers.length === 1 ? servers[0]?.id ?? '' : ''));
    setServiceName(instance?.serviceName ?? '');
    setPort(instance?.port?.toString() ?? '');
    setEditing(false);
  }, [instance?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    if (!serverId) return;
    onSave({
      serviceDefinitionId: definition.id,
      serverId,
      serviceName: serviceName || undefined,
      port: port ? parseInt(port, 10) : undefined,
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setServerId(instance?.serverId ?? (servers.length === 1 ? servers[0]?.id ?? '' : ''));
    setServiceName(instance?.serviceName ?? '');
    setPort(instance?.port?.toString() ?? '');
    setEditing(false);
  };

  // Display mode: show resolved values
  if (!editing && instance) {
    const serverName = servers.find((s) => s.id === instance.serverId)?.name ?? instance.serverId;
    return (
      <div className="mt-2 border-t border-border pt-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-content-secondary">Environment Override</p>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setEditing(true)} className="rounded px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1">
              Edit
            </button>
            {onDelete && (
              <button type="button" onClick={onDelete} className="rounded px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1">
                Remove
              </button>
            )}
          </div>
        </div>
        <div className="mt-1 grid grid-cols-3 gap-x-4 text-xs">
          <div>
            <span className="text-content-tertiary">Server:</span>{' '}
            <span className="text-content">{serverName}</span>
          </div>
          <div>
            <span className="text-content-tertiary">Service Name:</span>{' '}
            <span className={instance.serviceName ? 'text-content' : 'text-content-tertiary italic'}>
              {instance.serviceName || definition.serviceName || '—'}
            </span>
          </div>
          <div>
            <span className="text-content-tertiary">Port:</span>{' '}
            <span className={instance.port ? 'text-content' : 'text-content-tertiary italic'}>
              {instance.port ?? definition.port ?? '—'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Edit / create mode
  if (!editing && !instance) {
    return (
      <div className="mt-2 border-t border-border pt-2">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs font-medium text-brand-700 dark:text-brand-400 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 rounded px-1 py-0.5"
        >
          + Add environment override
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 border-t border-border pt-2 space-y-2">
      <p className="text-xs font-medium text-content-secondary">Environment Override</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-content-tertiary mb-0.5">Server *</label>
          <select
            value={serverId}
            onChange={(e) => setServerId(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          >
            <option value="">Select server...</option>
            {servers.map((srv) => (
              <option key={srv.id} value={srv.id}>{srv.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-content-tertiary mb-0.5">Service Name</label>
          <input
            type="text"
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            placeholder={definition.serviceName || 'Override service name'}
            className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
        <div>
          <label className="block text-xs text-content-tertiary mb-0.5">Port</label>
          <input
            type="number"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            placeholder={definition.port?.toString() || 'Override port'}
            min={1}
            max={65535}
            className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!serverId}
          className="rounded-md bg-brand-700 px-3 py-1 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
        >
          Save
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="rounded-md border border-border px-3 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


// --- Main Page ---

export function ProjectServersPage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';
  // --- Cached data from hooks ---
  const { data: environments = [], isLoading: envsLoading } = useEnvironments(key);
  const { data: serviceDefinitions = [], } = useServiceDefinitions(key);
  const { invalidateServers, invalidateServiceInstances, invalidateServiceDefinitions } = useInvalidateConfiguration(key);

  const { selectedEnvironmentId: selectedEnv, setSelectedEnvironmentId: setSelectedEnv } = useEnvironmentStore();

  // Auto-select first environment when loaded
  useEffect(() => {
    if (environments.length > 0 && !selectedEnv) {
      setSelectedEnv(environments[0]?.id ?? '');
    }
  }, [environments, selectedEnv, setSelectedEnv]);

  // --- Servers and instances (environment-scoped, cached) ---
  const { data: servers = [], isLoading: serversLoading, error: serversError } = useServers(key, selectedEnv);
  const { data: instances = [] } = useServiceInstances(key, selectedEnv);

  // --- Server status state ---
  const [serverStatuses, setServerStatuses] = useState<Record<string, ServerStatus>>({});
  const pollTimers = useRef<Record<string, ReturnType<typeof setTimeout> | undefined>>({});

const checkServerStatus = useCallback(async (server: Server) => {
    setServerStatuses((prev) => ({ ...prev, [server.id]: 'checking' }));
    try {
      const { runId } = await manageServer({
        projectKey: key,
        environment: selectedEnv,
        serverId: server.id,
        action: 'CHECK',
      });

      // Poll for the result
      const poll = async () => {
        try {
          const status = await getDeploymentStatus(runId);
          if (status.status === 'SUCCESS') {
            setServerStatuses((prev) => ({ ...prev, [server.id]: 'running' }));
          } else if (status.status === 'FAILED') {
            setServerStatuses((prev) => ({ ...prev, [server.id]: 'stopped' }));
          } else {
            // Still running/queued — poll again
            pollTimers.current[server.id] = setTimeout(poll, 3000);
          }
        } catch {
          setServerStatuses((prev) => ({ ...prev, [server.id]: 'unknown' }));
        }
      };
      pollTimers.current[server.id] = setTimeout(poll, 2000);
    } catch {
      setServerStatuses((prev) => ({ ...prev, [server.id]: 'unknown' }));
    }
  }, [projectKey, selectedEnv]);

  const checkAllServers = useCallback(() => {
    for (const server of servers) {
      checkServerStatus(server);
    }
  }, [servers, checkServerStatus]);

  // Check all servers on initial load and when servers/env change
  useEffect(() => {
    if (servers.length > 0 && selectedEnv) {
      checkAllServers();
    }
    return () => {
      // Cleanup poll timers
      for (const timer of Object.values(pollTimers.current)) {
        clearTimeout(timer);
      }
      pollTimers.current = {};
    };
  }, [servers, selectedEnv]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Service status state ---
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, ServerStatus>>({});
  const servicePollTimers = useRef<Record<string, ReturnType<typeof setTimeout> | undefined>>({});

  const parseServiceStatusOutput = useCallback((log: string, serviceNames: string[]): Record<string, 'running' | 'stopped'> => {
    const result: Record<string, 'running' | 'stopped'> = {};
    for (const name of serviceNames) {
      const activePattern = new RegExp(name + ':active');
      if (activePattern.test(log)) {
        result[name] = 'running';
      } else {
        result[name] = 'stopped';
      }
    }
    return result;
  }, []);

  const checkServicesForServer = useCallback(async (serverId: string) => {
    const serverInstances = instances.filter((inst) => inst.serverId === serverId);
    if (serverInstances.length === 0) return;

    setServiceStatuses((prev) => {
      const updated = { ...prev };
      for (const inst of serverInstances) {
        updated[inst.id] = 'checking';
      }
      return updated;
    });

    const serviceNames = serverInstances.map((inst) => inst.serviceName || inst.id);

    try {
      const { runId } = await manageService({
        projectKey: key,
        environment: selectedEnv,
        serviceInstanceIds: serviceNames,
        action: 'CHECK',
      });

      const poll = async () => {
        try {
          const status = await getDeploymentStatus(runId);
          if (status.status === 'SUCCESS' || status.status === 'FAILED') {
            try {
              const detail = await getDeploymentDetail(runId);
              const log = detail.log ?? '';
              const parsed = parseServiceStatusOutput(log, serviceNames);

              setServiceStatuses((prev) => {
                const updated = { ...prev };
                for (const inst of serverInstances) {
                  const name = inst.serviceName || inst.id;
                  updated[inst.id] = parsed[name] === 'running' ? 'running' : 'stopped';
                }
                return updated;
              });
            } catch {
              setServiceStatuses((prev) => {
                const updated = { ...prev };
                for (const inst of serverInstances) {
                  updated[inst.id] = 'unknown';
                }
                return updated;
              });
            }
          } else {
            servicePollTimers.current[serverId] = setTimeout(poll, 3000);
          }
        } catch {
          setServiceStatuses((prev) => {
            const updated = { ...prev };
            for (const inst of serverInstances) {
              updated[inst.id] = 'unknown';
            }
            return updated;
          });
        }
      };
      servicePollTimers.current[serverId] = setTimeout(poll, 2000);
    } catch {
      setServiceStatuses((prev) => {
        const updated = { ...prev };
        for (const inst of serverInstances) {
          updated[inst.id] = 'unknown';
        }
        return updated;
      });
    }
  }, [instances, projectKey, selectedEnv, parseServiceStatusOutput]);

  // When a server status resolves, update service statuses accordingly
  useEffect(() => {
    for (const server of servers) {
      const status = serverStatuses[server.id];
      if (status === 'running') {
        checkServicesForServer(server.id);
      } else if (status === 'stopped') {
        const serverInstances = instances.filter((inst) => inst.serverId === server.id);
        setServiceStatuses((prev) => {
          const updated = { ...prev };
          for (const inst of serverInstances) {
            updated[inst.id] = 'stopped';
          }
          return updated;
        });
      }
    }
    return () => {
      for (const timer of Object.values(servicePollTimers.current)) {
        clearTimeout(timer);
      }
      servicePollTimers.current = {};
    };
  }, [serverStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- UI state ---
  const [showServerForm, setShowServerForm] = useState(false);
  const [editingServer, setEditingServer] = useState<Server | null>(null);
  const [showServiceDefForm, setShowServiceDefForm] = useState(false);
  const [editingServiceDef, setEditingServiceDef] = useState<ServiceDefinition | null>(null);

  // --- Server actions ---
  const handleAddServer = async (data: Partial<Server>) => {
    await createServer(key, selectedEnv, data as Omit<Server, 'id' | 'environmentId' | 'projectKey'>);
    setShowServerForm(false);
    invalidateServers(selectedEnv);
  };

  const handleEditServer = async (data: Partial<Server>) => {
    if (!editingServer) return;
    await updateServer(key, selectedEnv, editingServer.id, data);
    setEditingServer(null);
    invalidateServers(selectedEnv);
  };

  const handleDeleteServer = async (server: Server) => {
    if (!confirm(`Remove server "${server.name}"?`)) return;
    await deleteServer(key, selectedEnv, server.id);
    invalidateServers(selectedEnv);
  };

  // --- Service definition actions ---
  const handleAddServiceDefinition = async (data: { name: string; port?: number; type?: string; serviceName?: string }) => {
    await createServiceDefinition(key, data);
    await invalidateServiceDefinitions();
    setShowServiceDefForm(false);
  };

  const handleUpdateServiceDefinition = async (data: { name: string; port?: number; type?: string; serviceName?: string }) => {
    if (!editingServiceDef) return;
    await updateServiceDefinition(key, editingServiceDef.id, data);
    await invalidateServiceDefinitions();
    setEditingServiceDef(null);
  };

  const handleDeleteServiceDefinition = async (def: ServiceDefinition) => {
    if (!confirm(`Delete service definition "${def.name}"? This cannot be undone.`)) return;
    await deleteServiceDefinition(key, def.id);
    await invalidateServiceDefinitions();
  };

  // --- Service instance (override) actions ---
  const handleSaveOverride = async (data: { serviceDefinitionId: string; serverId: string; serviceName?: string; port?: number }) => {
    // Check if an instance already exists for this definition in the selected environment
    const existing = instances.find((inst) => inst.serviceDefinitionId === data.serviceDefinitionId);
    if (existing) {
      await updateServiceInstance(key, selectedEnv, existing.id, data);
    } else {
      await createServiceInstance(key, selectedEnv, data);
    }
    invalidateServiceInstances(selectedEnv);
  };

  const handleDeleteOverride = async (instance: ServiceInstance) => {
    await deleteServiceInstance(key, selectedEnv, instance.id);
    invalidateServiceInstances(selectedEnv);
  };

  // --- Server & service actions (start/stop/restart) ---
  const handleServerAction = async (server: Server, action: 'start' | 'stop' | 'restart') => {
    const actionMap: Record<'start' | 'stop' | 'restart', ServerAction> = { start: 'START', stop: 'STOP', restart: 'REBOOT' };
    try {
      await manageServer({
        projectKey: key, 
        environment: selectedEnv,
        serverId: server.id,
        action: actionMap[action],
      });
    } catch (err) {
      alert(`Failed to ${action} server "${server.name}": ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleServiceAction = async (def: ServiceDefinition, action: 'start' | 'stop' | 'restart') => {
    const actionMap: Record<'start' | 'stop' | 'restart', ServiceAction> = { start: 'START', stop: 'STOP', restart: 'RESTART' };
    // Resolve the effective service name (instance override or definition default)
    const instance = instances.find((inst) => inst.serviceDefinitionId === def.id);
    const effectiveServiceName = instance?.serviceName || def.serviceName || def.name;
    const serverId = instance?.serverId;
    try {
      await manageService({
        projectKey: key,
        environment: selectedEnv,
        serviceInstanceIds: [effectiveServiceName],
        action: actionMap[action],
        serverId,
      });
    } catch (err) {
      alert(`Failed to ${action} service "${def.name}": ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // --- Helpers ---
  const getServerName = (srvId: string) => servers.find((s) => s.id === srvId)?.name ?? srvId;


  return (
    <main id="main-content" className="mx-auto max-w-5xl">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-content">Servers</h1>
        <p className="mt-1 text-sm text-content-secondary">
          Manage servers and service states for your environments
        </p>
      </header>

      {/* Environment selector */}
      <div className="mb-4">
        <EnvironmentSelector projectKey={key} />
      </div>

      {/* Page content */}
      <div className="space-y-6">
      {!selectedEnv && !envsLoading && (
        <EmptyState message="No environments available. Create an environment first." />
      )}

      {selectedEnv && (
        <>
          {/* -- Servers Section -- */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content">Servers</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={checkAllServers}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh State
                </button>
                <button
                  type="button"
                  onClick={() => setShowServerForm(true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Server
                </button>
              </div>
            </div>

            {serversLoading && <LoadingState />}
            {serversError && (
              <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm text-red-700 dark:text-red-300">Failed to load servers.</p>
              </div>
            )}

            {!serversLoading && !serversError && servers.length === 0 && !showServerForm && (
              <EmptyState message="No servers configured for this environment." />
            )}

            {showServerForm && (
              <div className="mb-3">
                <ServerForm onSave={handleAddServer} onCancel={() => setShowServerForm(false)} />
              </div>
            )}

            {!serversLoading && servers.length > 0 && (
              <ul className="space-y-2">
                {servers.map((srv) =>
                  editingServer?.id === srv.id ? (
                    <li key={srv.id}>
                      <ServerForm initial={srv} onSave={handleEditServer} onCancel={() => setEditingServer(null)} />
                    </li>
                  ) : (
                    <li key={srv.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <StatusIndicator status={serverStatuses[srv.id] ?? 'unknown'} />
                            <p className="text-sm font-medium text-content">{srv.name}</p>
                            {srv.ansibleOs && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                {srv.ansibleOs === 'windows' ? 'WinRM' : 'SSH'}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-content-secondary">
                            {srv.ansibleHost && <span>Host: {srv.ansibleHost}</span>}
                            {srv.ansibleUser && <span>User: {srv.ansibleUser}</span>}
                            {srv.type && <span>Type: {srv.type}</span>}
                            {srv.awsInstanceId && <span>Instance: {srv.awsInstanceId}</span>}
                            {srv.awsRegion && <span>Region: {srv.awsRegion}</span>}
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <ActionButton label="Start" onClick={() => handleServerAction(srv, 'start')} />
                          <ActionButton label="Stop" onClick={() => handleServerAction(srv, 'stop')} />
                          <ActionButton label="Restart" onClick={() => handleServerAction(srv, 'restart')} />
                          <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
                          <ActionButton label="Edit" onClick={() => setEditingServer(srv)} />
                          <ActionButton label="Remove" onClick={() => handleDeleteServer(srv)} variant="danger" />
                        </div>
                      </div>
                    </li>
                  )
                )}
              </ul>
            )}
          </section>

          {/* -- Services Section (definitions as primary, with per-environment overrides) -- */}
          <section>
            <SectionHeader title="Services" onAdd={() => { setShowServiceDefForm(true); setEditingServiceDef(null); }} addLabel="Add Service" />

            {serviceDefinitions.length === 0 && !showServiceDefForm && (
              <EmptyState message="No services defined for this project. Add one to get started." />
            )}

            {showServiceDefForm && !editingServiceDef && (
              <div className="mb-3">
                <ServiceDefinitionForm
                  onSave={handleAddServiceDefinition}
                  onCancel={() => setShowServiceDefForm(false)}
                />
              </div>
            )}

            {serviceDefinitions.length > 0 && (
              <ul className="space-y-2">
                {serviceDefinitions.map((def) => {
                  const instance = instances.find((inst) => inst.serviceDefinitionId === def.id);
                  return editingServiceDef?.id === def.id ? (
                    <li key={def.id}>
                      <ServiceDefinitionForm
                        initial={def}
                        onSave={handleUpdateServiceDefinition}
                        onCancel={() => setEditingServiceDef(null)}
                      />
                    </li>
                  ) : (
                    <li key={def.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <StatusIndicator status={instance ? (serviceStatuses[instance.id] ?? 'unknown') : 'unknown'} />
                            <p className="text-sm font-medium text-content">{def.name}</p>
                            {def.type && (
                              <span className="inline-flex items-center rounded-full bg-brand-100 dark:bg-brand-900/30 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                                {def.type}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-content-secondary">
                            {def.serviceName && <span>Service: {def.serviceName}</span>}
                            {def.port != null && <span>Port: {def.port}</span>}
                            {instance && <span>Server: {getServerName(instance.serverId)}</span>}
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <ActionButton label="Start" onClick={() => handleServiceAction(def, 'start')} />
                          <ActionButton label="Stop" onClick={() => handleServiceAction(def, 'stop')} />
                          <ActionButton label="Restart" onClick={() => handleServiceAction(def, 'restart')} />
                          <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
                          <ActionButton label="Edit" onClick={() => { setEditingServiceDef(def); setShowServiceDefForm(false); }} />
                          <ActionButton label="Delete" onClick={() => handleDeleteServiceDefinition(def)} variant="danger" />
                        </div>
                      </div>
                      {/* Per-environment override panel */}
                      <ServiceOverridePanel
                        definition={def}
                        servers={servers}
                        instance={instance}
                        onSave={handleSaveOverride}
                        onDelete={instance ? () => handleDeleteOverride(instance) : undefined}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
    </main>
  );
}
